import { apiClient } from '@/lib/api-client';
import { authStore } from '@/stores/auth-store';

export interface ImpersonationGrant {
  impersonation_session_id: string;
  access_token: string;
  token_type: string;
  expires_in: number;
  read_only: boolean;
  customer_id: string;
  subject_email: string;
  actor_email: string;
}

/**
 * Starting and ending a read-only session as a customer.
 *
 * Both calls are made with the *administrator's* token. Starting is obvious; stopping matters
 * more, because the impersonation token is read-only and a POST made with it would be refused —
 * so the local session is restored first and the audit record closed as the administrator.
 */
export const impersonationApi = {
  start: (customerId: string, reason: string) =>
    apiClient.post<ImpersonationGrant>('/admin/impersonation', {
      customer_id: customerId,
      reason,
    }),

  /**
   * Restores the administrator's session, then closes the audit record.
   *
   * The local restore happens first and unconditionally: if the network call fails, the
   * administrator must still get out. A session left open in the audit trail is a reporting
   * inaccuracy; an administrator trapped inside a customer's account is an incident.
   */
  stop: async () => {
    const { impersonation } = authStore.getState();
    if (impersonation === null) return;

    authStore.getState().endImpersonation();

    try {
      await apiClient.post('/admin/impersonation/stop', {
        impersonation_session_id: impersonation.sessionId,
      });
    } catch {
      // Swallowed on purpose — see above. The token expires within the half-hour regardless,
      // so the session cannot outlive its record by long.
    }
  },
};
